import React, { useState } from 'react';
import { X, Calendar, Upload, FileText, CheckCircle2, Clock, MoreHorizontal, Umbrella, Stethoscope, Briefcase, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LeaveRequestModal = ({ isOpen, onClose, employeeId = 'EMP-0007' }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    type: 'Congé annuel',
    startDate: '',
    endDate: '',
    halfDay: false,
    reason: '',
    file: null,
  });

  if (!isOpen) return null;

  const leaveTypes = [
    { id: 'Congé annuel', icon: <Umbrella size={24} className="text-violet-500" />, label: 'Congé annuel', color: 'border-violet-200 bg-violet-50 text-violet-700' },
    { id: 'Congé maladie', icon: <Stethoscope size={24} className="text-emerald-500" />, label: 'Congé maladie', color: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    { id: 'Congé exceptionnel', icon: <Sparkles size={24} className="text-orange-500" />, label: 'Congé exceptionnel', color: 'border-orange-200 bg-orange-50 text-orange-700' },
    { id: 'Congé sans solde', icon: <Briefcase size={24} className="text-gray-500" />, label: 'Congé sans solde', color: 'border-gray-200 bg-gray-50 text-gray-700' },
    { id: 'Récupération', icon: <Clock size={24} className="text-blue-500" />, label: 'Récupération', color: 'border-blue-200 bg-blue-50 text-blue-700' },
    { id: 'Autre absence', icon: <MoreHorizontal size={24} className="text-gray-500" />, label: 'Autre absence', color: 'border-gray-200 bg-gray-50 text-gray-700' },
  ];

  const handleNext = () => setStep(prev => Math.min(prev + 1, 3));
  const handleBack = () => setStep(prev => Math.max(prev - 1, 1));
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const token = sessionStorage.getItem('session_token');
      const pharmacyId = localStorage.getItem('pharmacy_id') || localStorage.getItem('selected_pharmacy');

      const currentUser = JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || '{}');

      const response = await fetch(`/api/employees/${employeeId}/leaves`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}`, 'X-Session-Token': token }),
          ...(pharmacyId && { 'X-Pharmacy-Id': pharmacyId }),
          ...(currentUser?.id && { 'X-User-Id': currentUser.id })
        },
        body: JSON.stringify({
          employee_id: employeeId,
          type: formData.type,
          start_date: formData.startDate,
          end_date: formData.endDate,
          duration_days: calculateDays(),
          reason: formData.reason,
          file_url: null
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la soumission');
      }

      onClose();
      setStep(1);
      
      // Dispatch an event so the parent component can reload data
      window.dispatchEvent(new Event('leaveRequestAdded'));
    } catch (error) {
      console.error('Error submitting leave request:', error);
      alert('Une erreur est survenue lors de la soumission.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffTime = Math.abs(end - start);
    let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    if (formData.halfDay) diffDays -= 0.5;
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRequested = calculateDays();
  const soldeAvant = 12; // Mock value
  const soldeApres = soldeAvant - daysRequested;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Nouvelle demande de congé</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-8 pt-6 pb-2">
          <div className="flex items-center justify-between relative">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-100 rounded-full"></div>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-violet-600 rounded-full transition-all duration-300" style={{ width: `${((step - 1) / 2) * 100}%` }}></div>
            
            {[1, 2, 3].map((s) => (
              <div key={s} className="relative z-10 flex flex-col items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  step >= s ? 'bg-violet-600 text-white' : 'bg-white text-gray-400 border-2 border-gray-200'
                }`}>
                  {s}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs font-medium text-gray-500">
            <span className={step >= 1 ? 'text-violet-700' : ''}>Type et dates</span>
            <span className={step >= 2 ? 'text-violet-700' : ''}>Informations complémentaires</span>
            <span className={step >= 3 ? 'text-violet-700' : ''}>Résumé et confirmation</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Type de congé</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {leaveTypes.map((type) => (
                    <div 
                      key={type.id}
                      onClick={() => setFormData({ ...formData, type: type.id })}
                      className={`cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center text-center gap-3 transition-all ${
                        formData.type === type.id ? type.color : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {type.icon}
                      <span className={`text-sm font-medium ${formData.type === type.id ? '' : 'text-gray-700'}`}>{type.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date de début</label>
                  <div className="relative">
                    <input 
                      type="date" 
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-500 focus:outline-none"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date de fin</label>
                  <div className="relative">
                    <input 
                      type="date" 
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-500 focus:outline-none"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-sm font-medium text-gray-700">Demi-journée</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={formData.halfDay} onChange={(e) => setFormData({ ...formData, halfDay: e.target.checked })} />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                </label>
              </div>

              <div className="p-4 bg-violet-50 border border-violet-100 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-violet-700">
                  <Calendar size={18} />
                  <span className="text-sm font-medium">Durée calculée</span>
                </div>
                <span className="font-bold text-violet-900">{daysRequested} jours ouvrés</span>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
                <textarea 
                  rows={4}
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Précisez le motif de votre demande..."
                  className="w-full border border-gray-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-violet-500 focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Pièce justificative <span className="text-gray-400 font-normal">(optionnel)</span></label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer">
                  <Upload size={24} className="text-violet-500 mb-2" />
                  <p className="text-sm font-medium text-violet-600">Ajouter un fichier</p>
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max 5 Mo)</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm">Récapitulatif</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Solde disponible avant demande</span>
                    <span className="font-medium text-gray-900">{soldeAvant} jours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Jours demandés</span>
                    <span className="font-medium text-gray-900">{daysRequested} jours</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                    <span className="font-semibold text-gray-900">Solde après validation (est.)</span>
                    <span className={`font-bold ${soldeApres >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {soldeApres} jours
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-violet-50 rounded-xl p-6 border border-violet-100 text-center">
                <div className="w-16 h-16 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={32} />
                </div>
                <h3 className="text-lg font-bold text-violet-900 mb-1">Demande prête à l'envoi</h3>
                <p className="text-sm text-violet-700">Veuillez vérifier les informations ci-dessous.</p>
              </div>

              <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                  <FileText className="text-gray-400" size={18} />
                  <h4 className="font-semibold text-gray-900 text-sm">Détails de la demande</h4>
                </div>
                <div className="p-5 space-y-3 text-sm">
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Employé</span>
                    <span className="col-span-2 font-medium text-gray-900">Ahmed Benali</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Type de congé</span>
                    <span className="col-span-2 font-medium text-gray-900">{formData.type}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Période</span>
                    <span className="col-span-2 font-medium text-gray-900">Du {formData.startDate} au {formData.endDate}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Durée</span>
                    <span className="col-span-2 font-medium text-gray-900">{daysRequested} jours ouvrés</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Solde avant</span>
                    <span className="col-span-2 font-medium text-gray-900">{soldeAvant} jours</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Solde après</span>
                    <span className="col-span-2 font-bold text-emerald-600">{soldeApres} jours</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Motif</span>
                    <span className="col-span-2 font-medium text-gray-900">{formData.reason || 'Aucun motif renseigné'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500">Justificatif</span>
                    <span className="col-span-2 font-medium text-gray-900">{formData.file ? formData.file.name : 'Aucun'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex items-center justify-between bg-gray-50 mt-auto">
          {step > 1 ? (
            <Button variant="outline" onClick={handleBack} className="bg-white border-gray-200">
              Retour
            </Button>
          ) : <div></div>}
          
          {step < 3 ? (
            <Button onClick={handleNext} className="bg-violet-600 hover:bg-violet-700 text-white min-w-[120px]">
              Suivant
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-violet-600 hover:bg-violet-700 text-white min-w-[120px]">
              {isSubmitting ? 'Envoi...' : 'Envoyer la demande'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestModal;
